import React, { useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import T from 'Components/Helpers/translate';
import FileResource from 'App/babel/resources/File';
import NotificationService from 'App/babel/NotificationService';
import type { EmailDraft } from './types';

type Attachment = NonNullable<EmailDraft['attachments']>[number];

type Props = {
	attachments: Attachment[];
	onChange: (attachments: Attachment[]) => void;
	/** Off once the draft is sent, cancelled or read-only: the list is then a record. */
	editable: boolean;
	onUploadingChange: (uploading: boolean) => void;
};

/**
 * The files an email draft carries. The agent can't always attach what its mail promises
 * (WEB-13259), so the rep can add or remove files before sending.
 */
const EmailDraftAttachments = ({ attachments, onChange, editable, onUploadingChange }: Props) => {
	const picker = useRef<HTMLInputElement>(null);
	const [uploading, setUploading] = useState(false);

	const attach = async (files: File[]) => {
		if (!files.length) return;
		setUploading(true);
		onUploadingChange(true);
		let next = attachments;
		for (const file of files) {
			try {
				const uploaded = await FileResource.upload(file, { fileEntity: 'mail' });
				next = [...next, { id: uploaded.id, name: uploaded.filename || file.name }];
				onChange(next);
			} catch (err) {
				console.error(`[EmailDraftAttachments] could not upload ${file.name}`, err);
				NotificationService.add({
					icon: 'times',
					style: NotificationService.style.ERROR,
					title: 'mail.uploadFail',
					body: err === 'file.tooLarge' ? 'file.tooLarge' : 'file.uploadFailed'
				});
			}
		}
		setUploading(false);
		onUploadingChange(false);
	};

	if (!editable && !attachments.length) return null;

	return (
		<>
			<dt>{T('mail.attachments')}</dt>
			<dd>
				{attachments.length ? (
					<ul className="pr-email-attachments">
						{attachments.map(a => (
							<li key={a.id}>
								<Paperclip style={{ width: 12, height: 12 }} />
								<span>{a.name}</span>
								{editable && (
									<button
										type="button"
										className="ws-btn ws-btn-secondary ws-prop-mini-btn"
										aria-label={`${T('default.remove')} ${a.name}`}
										disabled={uploading}
										onClick={() => onChange(attachments.filter(other => other.id !== a.id))}
									>
										<X style={{ width: 12, height: 12 }} />
									</button>
								)}
							</li>
						))}
					</ul>
				) : null}
				{editable && (
					<>
						<input
							ref={picker}
							type="file"
							multiple
							hidden
							onChange={e => {
								void attach(Array.from(e.target.files ?? []));
								// So picking the same file twice in a row still fires a change.
								e.target.value = '';
							}}
						/>
						<button
							type="button"
							className="ws-btn ws-btn-secondary ws-prop-mini-btn"
							disabled={uploading}
							onClick={() => picker.current?.click()}
						>
							<Paperclip style={{ width: 12, height: 12 }} />
							<span>{T('groupMail.addAttachment')}</span>
						</button>
					</>
				)}
			</dd>
		</>
	);
};

export default EmailDraftAttachments;
